import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { ArrowLeft, FileText, RefreshCw, Search, Info, AlertTriangle, XCircle, Loader2, ChevronLeft, ChevronRight, Folder } from "lucide-react";

const LEVELS = {
  info: { label: "Info", Icon: Info, cls: "text-sky-600" },
  warning: { label: "Warning", Icon: AlertTriangle, cls: "text-amber-600" },
  error: { label: "Error", Icon: XCircle, cls: "text-rose-600" },
};
const fmtBytes = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(2)} MB` : `${(b / 1024).toFixed(2)} KB`);
const fmtTs = (s) => (s ? s.replace("T", " ").slice(0, 19) : "");

function FileList({ files, sel, onSelect }) {
  return (
    <div className="space-y-1.5" data-testid="log-file-list">
      <button onClick={() => onSelect(null)} className={`w-full flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${!sel ? "border-indigo-400 bg-indigo-50 text-indigo-700" : "border-slate-200 hover:bg-slate-50 text-slate-600"}`} data-testid="log-file-all">
        <Folder className="w-4 h-4" /> All logs
      </button>
      {files.map((f) => {
        const on = sel && sel.channel === f.channel && sel.day === f.day;
        return (
          <button key={f.name} onClick={() => onSelect(f)} data-testid={`log-file-${f.name}`}
            className={`w-full flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-[13px] transition-colors ${on ? "border-indigo-400 bg-indigo-50 text-indigo-800" : "border-slate-200 hover:bg-slate-50 text-slate-700"}`}>
            <span className="truncate">{f.name}</span>
            <span className="shrink-0 text-[11px] text-slate-400 tabular-nums">{fmtBytes(f.bytes)}</span>
          </button>
        );
      })}
      {!files.length && <div className="text-xs text-slate-400 px-1 py-4">No logs captured yet.</div>}
    </div>
  );
}

function EntryRow({ e, n }) {
  const [open, setOpen] = useState(false);
  const L = LEVELS[e.level] || LEVELS.info;
  return (
    <>
      <tr onClick={() => setOpen((v) => !v)} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`log-row-${n}`}>
        <td className="px-3 py-2 whitespace-nowrap"><span className={`inline-flex items-center gap-1.5 text-[13px] ${L.cls}`}><L.Icon className="w-4 h-4" />{L.label}</span></td>
        <td className="px-3 py-2 whitespace-nowrap text-[13px] text-slate-700 tabular-nums">{fmtTs(e.ts)}</td>
        <td className="px-3 py-2 whitespace-nowrap text-[13px] text-slate-500">{e.channel}</td>
        <td className="px-3 py-2 text-[13px] text-slate-700 font-mono break-all">{e.message}</td>
        <td className="px-3 py-2 whitespace-nowrap text-[12px] text-slate-400">{e.user_email || ""}</td>
      </tr>
      {open && (
        <tr className="bg-slate-50" data-testid={`log-row-detail-${n}`}>
          <td colSpan={5} className="px-4 py-2"><pre className="text-[11px] text-slate-600 whitespace-pre-wrap break-all">{JSON.stringify(e, null, 2)}</pre></td>
        </tr>
      )}
    </>
  );
}

export default function LogViewer() {
  const { isPlatformAdmin } = useAuth();
  const [files, setFiles] = useState([]);
  const [sel, setSel] = useState(null);
  const [level, setLevel] = useState("");
  const [q, setQ] = useState("");
  const [qApplied, setQApplied] = useState("");
  const [order, setOrder] = useState("desc");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadFiles = () => api.get("/platform/logs/files").then((r) => setFiles(r.data.files || [])).catch(() => setFiles([]));
  const load = () => {
    setLoading(true);
    api.get("/platform/logs", { params: { channel: sel?.channel || "", day: sel?.day || "", level, q: qApplied, order, page, page_size: pageSize } })
      .then((r) => setData(r.data)).catch(() => setData({ entries: [], total: 0, levels: {} })).finally(() => setLoading(false));
  };
  useEffect(() => { if (isPlatformAdmin) loadFiles(); }, [isPlatformAdmin]);
  useEffect(() => { if (isPlatformAdmin) load(); /* eslint-disable-next-line */ }, [sel, level, qApplied, order, page, pageSize, isPlatformAdmin]);

  if (!isPlatformAdmin) return <div className="p-8 text-sm text-slate-500" data-testid="log-viewer-forbidden">Platform owner access required.</div>;
  const pages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;
  const lv = data?.levels || {};
  const allCount = Object.values(lv).reduce((a, b) => a + b, 0);

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="log-viewer-page">
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        <aside>
          <Link to="/" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800"><ArrowLeft className="w-3.5 h-3.5" /> Back to TedOps</Link>
          <h1 className="mt-1 text-2xl font-bold text-indigo-700 flex items-center gap-2"><FileText className="w-6 h-6" /> Log Viewer</h1>
          <p className="text-[11px] text-slate-400 mt-1 mb-4">Kept for 30 days · platform owners only</p>
          <div className="lg:max-h-[calc(100vh-200px)] lg:overflow-y-auto pr-1"><FileList files={files} sel={sel} onSelect={(f) => { setSel(f); setPage(1); }} /></div>
        </aside>
        <section className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <select value={level} onChange={(e) => { setLevel(e.target.value); setPage(1); }} className="rounded-lg border border-slate-200 bg-white px-3 h-9 text-sm" data-testid="log-level-filter">
              <option value="">{allCount.toLocaleString()} entries · all levels</option>
              {Object.entries(LEVELS).map(([k, v]) => <option key={k} value={k}>{(lv[k] || 0).toLocaleString()} · {v.label}</option>)}
            </select>
            <form className="relative flex-1 min-w-[200px]" onSubmit={(e) => { e.preventDefault(); setQApplied(q); setPage(1); }}>
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={sel ? `Search in "${sel.name}"` : "Search all logs (message, path, user, company)"} className="pl-9 h-9" data-testid="log-search" />
            </form>
            <button onClick={() => { loadFiles(); load(); }} className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50" title="Refresh" data-testid="log-refresh"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></button>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full min-w-[760px]">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs text-slate-500">
                  <th className="px-3 py-2 font-medium">Level</th><th className="px-3 py-2 font-medium">Time</th>
                  <th className="px-3 py-2 font-medium">Channel</th><th className="px-3 py-2 font-medium">Description</th>
                  <th className="px-3 py-2 font-medium text-right">
                    <span className="inline-flex items-center gap-2">
                      <select value={order} onChange={(e) => setOrder(e.target.value)} className="bg-transparent text-xs" data-testid="log-order">
                        <option value="desc">Newest first</option><option value="asc">Oldest first</option></select>
                      <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className="bg-transparent text-xs" data-testid="log-page-size">
                        <option value={25}>25 per page</option><option value={50}>50 per page</option></select>
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data === null ? <tr><td colSpan={5} className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-slate-400" /></td></tr>
                  : data.entries.length === 0 ? <tr><td colSpan={5} className="py-10 text-center text-sm text-slate-400" data-testid="log-empty">No log entries.</td></tr>
                  : data.entries.map((e, i) => <EntryRow key={i} n={i} e={e} />)}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
            <span data-testid="log-total">{(data?.total || 0).toLocaleString()} entries</span>
            <span className="inline-flex items-center gap-2">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="p-1 rounded border border-slate-200 disabled:opacity-40" data-testid="log-prev"><ChevronLeft className="w-4 h-4" /></button>
              <span data-testid="log-page">Page {page} of {pages}</span>
              <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="p-1 rounded border border-slate-200 disabled:opacity-40" data-testid="log-next"><ChevronRight className="w-4 h-4" /></button>
            </span>
          </div>
        </section>
      </div>
    </div>
  );
}
